import type { CSSProperties } from 'react';

export type UpsellWidgetTheme = 'light' | 'dark';
export type UpsellWidgetRadius = 'square' | 'soft' | 'rounded' | 'pill';
export type UpsellWidgetFont = 'inter' | 'roboto' | 'open-sans' | 'montserrat' | 'poppins' | 'lato';
export type UpsellWidgetTextSize = 'sm' | 'md' | 'lg';

export interface UpsellWidgetStyle {
  theme: UpsellWidgetTheme;
  buttonColor: string;
  buttonTextColor: string;
  radius: UpsellWidgetRadius;
  font: UpsellWidgetFont;
  textSize: UpsellWidgetTextSize;
}

export const DEFAULT_UPSELL_WIDGET_STYLE: UpsellWidgetStyle = {
  theme: 'light',
  buttonColor: '#1E5BFF',
  buttonTextColor: '#FFFFFF',
  radius: 'soft',
  font: 'inter',
  textSize: 'md',
};

const RADIUS_PX: Record<UpsellWidgetRadius, string> = { square: '0px', soft: '10px', rounded: '16px', pill: '999px' };
const TEXT_SIZE_PX: Record<UpsellWidgetTextSize, string> = { sm: '14px', md: '16px', lg: '18px' };
const FALLBACK_FONTS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
// Weights requested from Google Fonts; Lato has no 600, so the button falls back to 700.
const FONTS: Record<UpsellWidgetFont, { family: string; weights: string }> = {
  inter: { family: 'Inter', weights: '400;500;600;700' },
  roboto: { family: 'Roboto', weights: '400;500;700' },
  'open-sans': { family: 'Open Sans', weights: '400;600;700' },
  montserrat: { family: 'Montserrat', weights: '400;600;700' },
  poppins: { family: 'Poppins', weights: '400;600;700' },
  lato: { family: 'Lato', weights: '400;700' },
};
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function oneOf<T extends string>(value: unknown, options: readonly T[]): value is T {
  return typeof value === 'string' && (options as readonly string[]).includes(value);
}

/** Uses the seller style only when every field is valid; otherwise the VendePay default. */
export function resolveUpsellWidgetStyle(value: unknown): UpsellWidgetStyle {
  if (typeof value !== 'object' || value === null) return DEFAULT_UPSELL_WIDGET_STYLE;
  const style = value as Partial<Record<keyof UpsellWidgetStyle, unknown>>;
  if (
    !oneOf(style.theme, ['light', 'dark']) ||
    typeof style.buttonColor !== 'string' || !HEX_COLOR.test(style.buttonColor) ||
    typeof style.buttonTextColor !== 'string' || !HEX_COLOR.test(style.buttonTextColor) ||
    !oneOf(style.radius, Object.keys(RADIUS_PX) as UpsellWidgetRadius[]) ||
    !oneOf(style.font, Object.keys(FONTS) as UpsellWidgetFont[]) ||
    !oneOf(style.textSize, Object.keys(TEXT_SIZE_PX) as UpsellWidgetTextSize[])
  ) {
    return DEFAULT_UPSELL_WIDGET_STYLE;
  }
  return {
    theme: style.theme,
    buttonColor: style.buttonColor,
    buttonTextColor: style.buttonTextColor,
    radius: style.radius,
    font: style.font,
    textSize: style.textSize,
  };
}

export function upsellWidgetCssVariables(style: UpsellWidgetStyle): CSSProperties {
  return {
    '--vp-widget-button': style.buttonColor,
    '--vp-widget-button-text': style.buttonTextColor,
    '--vp-widget-radius': RADIUS_PX[style.radius],
    '--vp-widget-font': `'${FONTS[style.font].family}', ${FALLBACK_FONTS}`,
    '--vp-widget-font-size': TEXT_SIZE_PX[style.textSize],
  } as CSSProperties;
}

/** Inter is already loaded by index.html; other fonts are added once on demand. */
export function ensureUpsellWidgetFont(font: UpsellWidgetFont): void {
  if (font === 'inter') return;
  const id = `vp-widget-font-${font}`;
  if (document.getElementById(id) !== null) return;
  const { family, weights } = FONTS[font];
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@${weights}&display=swap`;
  document.head.appendChild(link);
}
